import { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Menu } from 'shared/ui'
import { MenuGroup } from 'shared/ui/Menu/ui/MenuGroup'
import { uiStore } from 'shared/store/uiStore'
import { TrialProgress } from 'entities/Subscription/ui/TrialProgress'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { checkAccessPage, EnumAccessPageKeys } from 'entities/ExportRouters/lib/checkAccessPage'
import { MediaLibraryStore } from 'widgets/MediaLibrary'
import {
  type IMediaLibraryStoreProps,
  MediaLibrarySelectMode,
  MediaLibraryVariant,
} from 'widgets/MediaLibrary/types/types'
import { usePresentationModeContext } from 'widgets/PresentationMode'
import { KnowledgeBaseRoutes } from 'pages/settings/pages/knowledgeBase'
import {
  canceledDeveloperRolesMap,
  canceledPersonalRolesMap,
  canceledProductRolesMap,
  canceledSystemRolesMap,
  canceledWorkspaceRolesMap,
  developerRolesMap,
  getDeveloperItems,
  getMenuItemsForRole,
  getPersonalAllItems,
  getProductAllItems,
  getSystemItems,
  getWorkspaceAllItems,
  personalRolesMap,
  productRolesMap,
  systemRolesMap,
  workspaceRolesMap,
} from './menuItemLists'
import styles from './styles.module.scss'

export const SettingsMenu = observer(() => {
  const entities = useEntitiesContext()
  const presentationStore = usePresentationModeContext()
  const { isCanceled } = entities.subscriptionStore
  const role = entities.usersStore.user?.role

  const canManageMembers = entities.userPermissionsStore.getItem('can_manage_members')
  const canManageTeams = checkAccessPage(EnumAccessPageKeys.teams, entities)

  const canManageBillingSettings = entities.userPermissionsStore.getItem(
    'can_manage_billing_settings'
  )
  const canManageIntegrations = entities.userPermissionsStore.can_manage_integrations
  const canAddSubscription = isCanceled && canManageBillingSettings

  useEffect(() => {
    if (window.RouterVue) {
      const unsubscribe = window.RouterVue.afterEach((to) => {
        uiStore.changeRoute({
          path: to.path,
          options: {
            state: to.params,
          },
        })
      })

      return () => {
        if (unsubscribe) {
          unsubscribe()
        }
      }
    }
  }, [])

  const mediaLibraryConfiguration: IMediaLibraryStoreProps = {
    mediaLibraryVariant: MediaLibraryVariant.Default,
    onAddAttachment: () => {},
    selectMode: MediaLibrarySelectMode.Multiple,
  }

  const [mediaLibraryStore] = useState(
    () => new MediaLibraryStore(mediaLibraryConfiguration, entities, presentationStore)
  )

  const workspaceAllItems = getWorkspaceAllItems(
    {
      canManageMembers,
      canManageBillingSettings,
      canAddSubscription,
      canManageIntegrations,
      canManageTeams,
    },
    entities
  )
  const productAllItems = getProductAllItems(mediaLibraryStore, entities).filter((item) => {
    if (item.type === 'nav' && item.to === KnowledgeBaseRoutes.root) return false
    if (item.key === 'email_to_sms') {
      return checkAccessPage(EnumAccessPageKeys.settings_email_to_sms, entities)
    }

    return true
  })

  const personalAllItems = getPersonalAllItems()
  const developerAllItems = getDeveloperItems(entities)

  const actualWorkspaceRolesMap = isCanceled ? canceledWorkspaceRolesMap : workspaceRolesMap
  const actualProductRolesMap = isCanceled ? canceledProductRolesMap : productRolesMap
  const actualPersonalRolesMap = isCanceled ? canceledPersonalRolesMap : personalRolesMap
  const actualDeveloperRolesMap = isCanceled ? canceledDeveloperRolesMap : developerRolesMap
  const actualSystemRolesMap = isCanceled ? canceledSystemRolesMap : systemRolesMap

  const workspaceItems = role
    ? getMenuItemsForRole(role, workspaceAllItems, actualWorkspaceRolesMap)
    : []
  const productItems = role ? getMenuItemsForRole(role, productAllItems, actualProductRolesMap) : []
  const personalItems = role
    ? getMenuItemsForRole(role, personalAllItems, actualPersonalRolesMap)
    : []
  const developerItems = role
    ? getMenuItemsForRole(role, developerAllItems, actualDeveloperRolesMap)
    : []

  const showSystemSection = entities.featureFlagsStore.system_health_dashboard
  const systemAllItems = getSystemItems()
  const systemItems = role ? getMenuItemsForRole(role, systemAllItems, actualSystemRolesMap) : []

  return (
    <Menu
      title='Settings'
      menuContent={
        <div>
          <MenuGroup title='Workspace' items={workspaceItems} className={styles.menuGroup} />
          <MenuGroup title='Product' items={productItems} className={styles.menuGroup} />
          <MenuGroup title='Personal' items={personalItems} className={styles.menuGroup} />
          {showSystemSection && (
            <MenuGroup withExpand title='System' items={systemItems} className={styles.menuGroup} />
          )}
          {!isCanceled && (
            <MenuGroup
              title='Developer'
              items={developerItems}
              className={styles.menuGroup}
              initOpen={false}
              withExpand
            />
          )}
        </div>
      }
      bottomNode={<TrialProgress />}
    />
  )
})
